import { useGet } from 'hooks/use-get';
import React, { useContext, useMemo } from 'react';
import { getFeed } from '../../self-service/features/feed-catalog/api/get-feed';

import { useAccountState } from 'contexts/account/account-context';
import { Feed } from 'views/self-service/features/feed-catalog/types/feed.type';
import { usePost } from 'hooks/use-post';
import { postLatestFeedingStatus } from 'views/production-systems/api/productionSystems';
import { FeedingStatus } from 'views/production-systems/features/farm-overview/types/feeding-status.type';
import { AxiosError } from 'axios';
import { UseMutationResult } from '@tanstack/react-query';
import { GetFeedingStatusInput } from 'views/production-systems/features/farm-overview/types/feeding-status-input.type';

type State = {
  isLoading: boolean;
  feedCodes: string[];
  allFeeds: Feed[] | undefined;
  feedingStatusQuery: UseMutationResult<FeedingStatus, AxiosError<unknown, any>, { globalSiteId: string; date: string; pondIds: string[]; }, unknown>
};

const FeedingContext = React.createContext<State | undefined>(undefined);

type FeedingProviderProps = {
  children: React.ReactNode;
};

const FeedingProvider: React.FC<FeedingProviderProps> = ({ children }) => {
  const { account } = useAccountState();

  const { data: allFeeds, isLoading } = useGet(['getFeed'], () => getFeed(String(account.company!.companyId)), {
    enabled: !!account.company!.companyId,
  });

  const feedCodes = useMemo(() => allFeeds?.map((feed) => feed?.code).filter((code): code is string => code != null) ?? [], [allFeeds]);

  const feedingStatusQuery = usePost<FeedingStatus, GetFeedingStatusInput>(
    (request) => postLatestFeedingStatus(request.globalSiteId, request),
  );

  return <FeedingContext.Provider value={{ feedCodes, allFeeds, isLoading, feedingStatusQuery }}>{children}</FeedingContext.Provider>;
};

function useFeedingState() {
  const context = useContext(FeedingContext);
  if (context === undefined) {
    throw new Error('useFeedingState must be used within a Feeding provider');
  }
  return context;
}

export { FeedingProvider, useFeedingState };
